/**
 * PROTOTYPE vertical fixtures (Phase P3). Static, hand-authored sample data
 * so the coaching-institute student experience can be built and verified
 * FE-first, distinct from the school dashboard.
 *
 * Replaced by real API reads when the coaching vertical's phases land
 * (Iron Rule #1). Nothing here is a source of truth.
 *
 * The study-centre fixtures that used to live here are **gone** as of Phase
 * SC-4b — seat card, focus/streak counters, "my books" progress bars, the
 * declared-subjects preview, the mock diagnostic and the mock SM-2 queue.
 * Study-centre learner surfaces now read the real `/api/learning/*`
 * endpoints and say plainly where a backend doesn't exist yet, rather than
 * previewing one. Anything added back here must be coaching-only.
 */
import {
  Atom,
  Calculator,
  LineChart,
  Medal,
  MessageCircleQuestion,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { JoyKey } from "./joy";

/* ══════════════════════════ Coaching institute ══════════════════════════ */

export interface BatchInfo {
  name: string;
  course: string;
  nextClass: string;
  icon: LucideIcon;
  color: JoyKey;
}

export interface TestSeriesItem {
  title: string;
  when: string;
  questions: number;
  status: "upcoming" | "live" | "done";
  score?: string;
  color: JoyKey;
}

export interface RankStat {
  label: string;
  value: string;
  delta?: string;
  icon: LucideIcon;
  color: JoyKey;
}

export const COACHING_BATCHES: BatchInfo[] = [
  {
    name: "JEE Advanced 2027",
    course: "Physics · Chemistry · Maths",
    nextClass: "Today, 5:00 PM · Rotational Motion",
    icon: Atom,
    color: "indigo",
  },
  {
    name: "Weekend Maths Booster",
    course: "Calculus intensive",
    nextClass: "Sat, 10:00 AM · Definite Integrals",
    icon: Calculator,
    color: "violet",
  },
];

export const COACHING_RANK: RankStat[] = [
  { label: "All-India rank", value: "#342", delta: "▲ 58", icon: TrendingUp, color: "green" },
  { label: "Batch rank", value: "#7", delta: "▲ 2", icon: Medal, color: "amber" },
  { label: "Percentile", value: "97.4", icon: LineChart, color: "sky" },
];

export const COACHING_TESTS: TestSeriesItem[] = [
  {
    title: "Full Syllabus Mock #12",
    when: "Tomorrow, 2:00 PM",
    questions: 90,
    status: "upcoming",
    color: "indigo",
  },
  {
    title: "Chemistry Part Test #8",
    when: "Live now · ends 6:00 PM",
    questions: 45,
    status: "live",
    color: "rose",
  },
  {
    title: "Physics Part Test #7",
    when: "Attempted 2 days ago",
    questions: 45,
    status: "done",
    score: "38 / 45",
    color: "green",
  },
];

export const COACHING_DOUBTS = {
  open: 2,
  resolvedThisWeek: 9,
  icon: MessageCircleQuestion,
};
